import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonModal,
  IonRow,
  IonTitle,
  IonToolbar,
  IonIcon,
} from "@ionic/react";
import {
  searchOutline,
  medicalOutline,
  filterOutline,
  homeOutline,
} from "ionicons/icons";
import CustomToast from "../../components/basicsComponents/CustomToast/CustomToast";
import StyledButton from "../../components/basicsComponents/StyledButton/StyledButton";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Typography,
} from "@mui/joy";
import Tooltip from "@mui/material/Tooltip";
import { Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { useEstudiosPaciente } from "./useEstudiosPaciente";
import { parseRtfToHtml, abrirEnNuevaVentana } from "./EstudiosPaciente-Utils";
import useTituloPagina from "../../hooks/tituloPagina";
import "./EstudiosPaciente.css";

export default function EstudiosPaciente() {
  const history = useHistory();
  useTituloPagina();

  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [toast, setToast] = useState({ open: false, mensaje: "", tipo: "" });
  const mostrarNotificacion = (abrir, mensaje, tipo) => {
    let notificacion = {};
    if (abrir) {
      notificacion = { open: true, mensaje: mensaje, tipo: tipo };
    } else {
      notificacion = { open: false, mensaje: "", tipo: "" };
    }
    setToast(notificacion);
  };

  const {
    usuario, setUsuario,
    cargando, setCargando,
    tituloPagina, setTituloPagina,
    urlAxioPP, setUrlAxio,
    estudioPdf, setEstudioPdf,
    listadoEstudios,
    listadoEstudiosFiltrados,
    abrirModal,
    abrirCerrarModal,
    traerEstudiosPaciente,
    DescargarPdf,
    VisualizarPdf,
  } = useEstudiosPaciente(mostrarNotificacion);

  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      try {
        const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
        if (sesion) {
          await traerEstudiosPaciente(sesion);
          setUsuario(sesion);
          setUrlAxio(localStorage.getItem("urlAxioPP"));
          setTituloPagina(localStorage.getItem("nombreClinica"));
          document.title = localStorage.getItem("tituloWeb");
        }
      } catch (e) {
        history.push({ pathname: "/ErrorPage" });
      } finally {
        setCargando(false);
      }
    };
    
    cargarDatos();
  }, []);

  const handleVolverInicio = () => {
    history.push("/portal/Inicio");
  };

  // Filtrar estudios por tipo
  const estudiosParaMostrar = listadoEstudiosFiltrados.filter(estudio => {
    return filtroTipo === "todos" || estudio.tipoEstudio === filtroTipo;
  });

  // Agrupar estudios filtrados
  const estudiosAgrupados = estudiosParaMostrar.reduce((acc, estudio) => {
    const tipo = estudio.tipoEstudio;
    if (!acc[tipo]) {
      acc[tipo] = [];
    }
    acc[tipo].push(estudio);
    return acc;
  }, {});

  const tiposEstudio = {};
  listadoEstudiosFiltrados.forEach(estudio => {
    tiposEstudio[estudio.tipoEstudio] = (tiposEstudio[estudio.tipoEstudio] || 0) + 1;
  });

  return (
    <>
      {cargando ? (
        <IonGrid>
          <IonRow className="ion-justify-content-center">
            <IonCol>
              <div className="ion-text-center estudios-spinner-container">
                <div className="estudios-spinner-flex">
                  <div className="estudios-spinner"></div>
                  <p className="estudios-spinner-text">Procesando...</p>
                </div>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      ) : listadoEstudios.length <= 0 ? (
        <IonGrid>
          <IonRow className="ion-justify-content-center">
            <IonCol>
              <div className="ion-text-center">
                <br/>
                <h3>No tiene estudios registrados aún</h3>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      ) : (
        <>
          {/* HEADER CON TÍTULO Y FILTROS */}
          <div className="estudios-header-bg">
            <div className="estudios-header">
              {/* Título Principal con Botón Volver */}
              <div className="estudios-header-title-row">
                <div className="estudios-header-icon">
                  <IonIcon icon={medicalOutline} className="estudios-header-icon-svg" />
                </div>
                <div className="estudios-header-title-flex">
                  <h1 className="estudios-header-title">Mis Estudios Médicos</h1>
                  <p className="estudios-header-subtitle">
                    {listadoEstudios.length} estudio{listadoEstudios.length !== 1 ? 's' : ''} disponible{listadoEstudios.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button className="estudios-header-btn-volver" onClick={handleVolverInicio}>
                  <IonIcon icon={homeOutline} className="estudios-header-btn-volver-icon" />
                  <span>Volver al inicio</span>
                </button>
              </div>

              {/* Filtros por tipo */}
              <div>
                <div className="estudios-filtros-row">
                  <IonIcon icon={filterOutline} className="estudios-filtros-icon" />
                  <span className="estudios-filtros-label">
                    Filtrar por tipo:
                  </span>
                </div>
                <div className="estudios-filtros-chips">
                  <Chip
                    label={`Todos (${listadoEstudios.length})`}
                    onClick={() => setFiltroTipo("todos")}
                    variant={filtroTipo === "todos" ? "filled" : "outlined"}
                    color={filtroTipo === "todos" ? "primary" : "default"}
                    className="estudios-chip"
                    style={{ fontWeight: filtroTipo === "todos" ? "600" : "500" }}
                  />
                  {Object.entries(tiposEstudio).map(([tipo, cantidad]) => {
                    const colores = {
                      "Colposcopia": "#8b5cf6",
                      "Histeroscopia": "#6366f1",
                      "Patología": "#f59e0b",
                      "Ecografía": "#10b981",
                      "Laboratorio": "#ef4444"
                    };
                    return (
                      <Chip
                        key={tipo}
                        label={`${tipo} (${cantidad})`}
                        onClick={() => setFiltroTipo(tipo)}
                        variant={filtroTipo === tipo ? "filled" : "outlined"}
                        className="estudios-chip"
                        style={{
                          backgroundColor: filtroTipo === tipo ? colores[tipo] : "transparent",
                          borderColor: colores[tipo],
                          color: filtroTipo === tipo ? "white" : colores[tipo],
                          fontWeight: filtroTipo === tipo ? "600" : "500"
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Indicador de resultados filtrados */}
              {filtroTipo !== "todos" && (
                <div className="estudios-filtros-indicador">
                  <span className="estudios-filtros-indicador-text">
                    {estudiosParaMostrar.length} {filtroTipo} encontrado{estudiosParaMostrar.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => setFiltroTipo("todos")}
                    className="estudios-filtros-limpiar-btn"
                  >
                    Limpiar filtro
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ACORDEONES DE ESTUDIOS */}
          <div className="estudios-acordeon-bg">
            {Object.entries(estudiosAgrupados).length === 0 ? (
              <div className="estudios-msg-vacio">
                <IonIcon icon={searchOutline} className="estudios-msg-vacio-icon" />
                <h3 className="estudios-msg-vacio-title">
                  No se encontraron estudios de este tipo
                </h3>
                <p className="estudios-msg-vacio-desc">
                  Intenta seleccionando otro filtro
                </p>
              </div>
            ) : (
              Object.entries(estudiosAgrupados).map(([tipoEstudio, estudios]) => {
              // Definir colores por tipo de estudio
              const getEstudioColor = (tipo) => {
                const colores = {
                  Histeroscopia: {
                    primary: "#6366f1",
                    secondary: "#e0e7ff",
                    icon: "🔬",
                  },
                  Patología: {
                    primary: "#f59e0b",
                    secondary: "#fef3c7",
                    icon: "📋",
                  },
                  Ecografía: {
                    primary: "#10b981",
                    secondary: "#d1fae5",
                    icon: "📡",
                  },
                  Laboratorio: {
                    primary: "#ef4444",
                    secondary: "#fee2e2",
                    icon: "🧪",
                  },
                  Colposcopia: {
                    primary: "#8b5cf6",
                    secondary: "#f3e8ff",
                    icon: "🔍",
                  },
                };
                return (
                  colores[tipo] || {
                    primary: "#64748b",
                    secondary: "#f1f5f9",
                    icon: "📄",
                  }
                );
              };

              const colorConfig = getEstudioColor(tipoEstudio);

              return (
                <AccordionGroup
                  key={tipoEstudio}
                  variant="plain"
                  sx={{ marginBottom: "24px" }}
                >
                  <Accordion
                    variant="plain"
                    sx={{
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      borderRadius: "12px",
                      overflow: "hidden",
                      backgroundColor: "white",
                    }}
                  >
                    {/* Header del tipo de estudio como AccordionSummary */}
                    <AccordionSummary
                      sx={{
                        background: `linear-gradient(135deg, ${colorConfig.primary} 0%, ${colorConfig.primary}dd 100%)`,
                        color: "white",
                        padding: "16px 20px",
                        minHeight: "72px",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${colorConfig.primary}ee 0%, ${colorConfig.primary}cc 100%)`,
                        },
                        "& .MuiAccordionSummary-button": {
                          cursor: "pointer",
                        },
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          width: "100%",
                        }}
                      >
                        <span style={{ fontSize: "24px" }}>
                          {colorConfig.icon}
                        </span>
                        <Typography
                          level="title-lg"
                          sx={{ color: "white", fontWeight: 600, flex: 1 }}
                        >
                          {tipoEstudio}
                        </Typography>
                        <div
                          style={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {estudios.length}{" "}
                          {estudios.length === 1 ? "estudio" : "estudios"}
                        </div>
                      </div>
                    </AccordionSummary>

                    {/* Lista de estudios como AccordionDetails */}
                    <AccordionDetails sx={{ padding: 0 }}>
                      <div className="estudios-acordeon-details-bg">
                        {estudios.map((estudio, index) => (
                          <div
                            key={estudio.codigoUI || estudio.codigo}
                            className="estudios-acordeon-item"
                            style={{
                              borderBottom:
                                index < estudios.length - 1
                                  ? `1px solid ${colorConfig.secondary}`
                                  : "none",
                              backgroundColor: "white"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                colorConfig.secondary;
                              e.currentTarget.style.transform =
                                "translateX(4px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "white";
                              e.currentTarget.style.transform = "translateX(0)";
                            }}
                          >
                            {/* Indicador visual */}
                            <div
                              className="estudios-acordeon-item-bar"
                              style={{ backgroundColor: colorConfig.primary }}
                            />

                            {/* Contenido del estudio */}
                            <div className="estudios-acordeon-item-content">
                              <Typography
                                level="title-sm"
                                className="estudios-acordeon-item-title"
                              >
                                {estudio.text.length > 25
                                  ? estudio.text.substring(0, 25) + "..."
                                  : estudio.text}
                              </Typography>
                              <Typography
                                level="body-xs"
                                className="estudios-acordeon-item-date"
                              >
                                📅 {estudio.fecha}
                                {estudio.formato && (
                                  <span
                                    className="estudios-acordeon-item-formato"
                                    style={{
                                      backgroundColor: colorConfig.secondary,
                                      color: colorConfig.primary
                                    }}
                                  >
                                    {estudio.formato}
                                  </span>
                                )}
                              </Typography>
                            </div>

                            {/* Acciones */}
                            <div className="estudios-acordeon-item-actions">
                              <Tooltip title="Visualizar" arrow placement="top">
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    VisualizarPdf(estudio);
                                  }}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    backgroundColor: colorConfig.primary,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 12px rgba(0,0,0,0.2)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                    e.currentTarget.style.boxShadow =
                                      "0 2px 8px rgba(0,0,0,0.1)";
                                  }}
                                >
                                  <VisibilityIcon
                                    sx={{ color: "white", fontSize: "20px" }}
                                  />
                                </div>
                              </Tooltip>

                              {!estudio.esLaboratorio &&
                                !estudio.esEcografia && (
                                  <Tooltip
                                    title="Descargar"
                                    arrow
                                    placement="top"
                                  >
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        DescargarPdf(estudio);
                                      }}
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        backgroundColor: "#64748b",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform =
                                          "scale(1.1)";
                                        e.currentTarget.style.backgroundColor =
                                          "#475569";
                                        e.currentTarget.style.boxShadow =
                                          "0 4px 12px rgba(0,0,0,0.2)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform =
                                          "scale(1)";
                                        e.currentTarget.style.backgroundColor =
                                          "#64748b";
                                        e.currentTarget.style.boxShadow =
                                          "0 2px 8px rgba(0,0,0,0.1)";
                                      }}
                                    >
                                      <DownloadIcon
                                        sx={{
                                          color: "white",
                                          fontSize: "20px",
                                        }}
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </AccordionGroup>
              );
            })
            )}
          </div>

          <IonModal
            isOpen={abrirModal}
            backdropDismiss={false}
            expand="block"
            className={"viewer-modal estudios-viewer-modal"}
            style={{
              "--width": estudioPdf.esEcografia ? "95%" : "98%",
              "--height": estudioPdf.esEcografia ? "90%" : "95%",
              "--max-width": estudioPdf.esEcografia ? "1200px" : "100vw",
              "--max-height": estudioPdf.esEcografia ? "800px" : "100vh",
              "--border-radius": estudioPdf.esEcografia ? "12px" : "8px"
            }}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>{`${estudioPdf.nombre} - ${estudioPdf.fecha}`}</IonTitle>
                <div slot="end" className="estudios-viewer-modal-actions">
                  {/* Botón para ampliar */}
                  {(estudioPdf.esLaboratorio ||
                    (!estudioPdf.esEcografia && estudioPdf.doc)) && (
                    <StyledButton
                      fill="clear"
                      onClick={() => {
                        const src = estudioPdf.esLaboratorio
                          ? `data:application/pdf;base64,${estudioPdf.doc}`
                          : estudioPdf.doc;
                        abrirEnNuevaVentana(
                          src,
                          "pdf",
                          `${estudioPdf.nombre} - ${estudioPdf.fecha}`
                        );
                      }}
                      className="amarillo-sol-text estudios-viewer-modal-ampliar"
                    >
                      🔍 Ampliar
                    </StyledButton>
                  )}
                  <StyledButton
                    strong={true}
                    onClick={() => abrirCerrarModal()}
                    fill="clear"
                    className="amarillo-sol-text estudios-viewer-modal-cerrar"
                  >
                    Cerrar
                  </StyledButton>
                </div>
              </IonToolbar>
            </IonHeader>
            <IonContent className={"viewer-content"}>
              {estudioPdf.esLaboratorio ? (
                // Mostrar PDF de Laboratorio con iframe mejorado
                <div className="viewer-container clickable-viewer">
                  <iframe
                    src={`data:application/pdf;base64,${estudioPdf.doc}`}
                    className="viewer-iframe"
                    title="Informe de Laboratorio"
                  />
                  <div
                    className="viewer-zoom-indicator"
                    onClick={() =>
                      abrirEnNuevaVentana(
                        `data:application/pdf;base64,${estudioPdf.doc}`,
                        "pdf",
                        `${estudioPdf.nombre} - ${estudioPdf.fecha}`
                      )
                    }
                    title="Click para ver a pantalla completa"
                  >
                    🔍 Click para ampliar
                  </div>
                </div>
              ) : estudioPdf.esEcografia ? (
                // Mostrar contenido RTF convertido a HTML para ecografías
                <div className="viewer-text-content">
                  {parseRtfToHtml(estudioPdf.doc)}
                </div>
              ) : (
                // Mostrar PDF para otros estudios con viewer mejorado
                estudioPdf.doc && (
                  <div className="viewer-container clickable-viewer">
                    <iframe
                      src={estudioPdf.doc}
                      className="viewer-iframe"
                      title={`${estudioPdf.nombre} - ${estudioPdf.fecha}`}
                    />
                    <div
                      className="viewer-zoom-indicator"
                      onClick={() =>
                        abrirEnNuevaVentana(
                          estudioPdf.doc,
                          "pdf",
                          `${estudioPdf.nombre} - ${estudioPdf.fecha}`
                        )
                      }
                      title="Click para ver a pantalla completa"
                    >
                      🔍 Click para ampliar
                    </div>
                  </div>
                )
              )}
            </IonContent>
          </IonModal>
          <CustomToast
            openToast={toast.open}
            onDidDismiss={(e) => mostrarNotificacion(false, "", "")}
            message={toast.mensaje}
            colorNotificacion={toast.tipo}
          />
        </>
      )}
    </>
  );
}
